// SPDX-License-Identifier: AGPL-3.0-or-later
// Copyright (C) 2026 Ben Richardson — https://benrichardson.dev
// Additional terms under AGPL-3.0 section 7(b) apply; see ADDITIONAL-TERMS.md.

import type { AlgoId, DitherParams, DitherResult, PaletteId } from './types';
import {
  ALGO_LABELS,
  PALETTE_LABELS,
  PRESETS,
  colorRange,
  isDiffusion,
  matchPreset,
  usesColorCount,
} from './presets';
import { formatBytes } from './imageutil';
import { clampInt } from './dither';
import { GLOSSARY } from './glossary';
import { mountEventTrail, eventCount } from './eventlog';

export interface UIHandlers {
  onFile: (file: File) => void;
  onSample: () => void;
  onChange: () => void;
  onReset: () => void;
  onDownload: () => void;
  onCopy: () => void;
  onShare: () => void;
}

export interface UI {
  getParams: () => DitherParams;
  setParams: (p: DitherParams) => void;
  showStage: (stage: 'empty' | 'working') => void;
  setBusy: (on: boolean, phase?: string, pct?: number) => void;
  showResult: (res: DitherResult, outUrl: string, rasterUrl: string) => void;
  setError: (msg: string) => void;
  clearError: () => void;
  toast: (msg: string) => void;
  setShareVisible: (on: boolean) => void;
  setFileName: (name: string) => void;
  closeOverlays: () => boolean;
}

const g = (t: string) =>
  `<span class="glossary-link" data-term="${t}" tabindex="0" role="button">${t}</span>`;

export function createUI(root: HTMLElement, h: UIHandlers): UI {
  root.innerHTML = SHELL;

  const $ = <T extends HTMLElement = HTMLElement>(sel: string) => root.querySelector(sel) as T;
  const stageEmpty = $('#stage-empty');
  const stageWorking = $('#stage-working');
  const dropzone = $('#dropzone');
  const fileInput = $<HTMLInputElement>('#file-input');
  const errorBar = $('#error-bar');
  const errorMsg = $('#error-msg');
  const busy = $('#busy');
  const busyLabel = $('#busy-label');
  const busyBar = $('#busy-bar');
  const compare = $('#compare');
  const rasterImg = $<HTMLImageElement>('#raster-img');
  const outImg = $<HTMLImageElement>('#out-img');
  const slider = $<HTMLInputElement>('#slider');
  const statBar = $('#stat-bar');
  const fileNameEl = $('#file-name');
  const presetRow = $('#preset-row');
  const shareBtn = $<HTMLButtonElement>('#act-share');
  const toastEl = $('#toast');
  const modalHost = $('#modal-host');
  const drawer = $('#drawer');
  const drawerTrail = $('#drawer-trail');

  // ── Drop zone + file input ────────────────────────────────────────────
  const openPicker = () => fileInput.click();
  dropzone.addEventListener('click', (e) => {
    if ((e.target as HTMLElement).closest('#sample-btn')) return;
    openPicker();
  });
  dropzone.addEventListener('keydown', (e) => {
    if (e.key === 'Enter' || e.key === ' ') {
      e.preventDefault();
      openPicker();
    }
  });
  fileInput.addEventListener('change', () => {
    const f = fileInput.files?.[0];
    if (f) h.onFile(f);
    fileInput.value = '';
  });
  $('#sample-btn').addEventListener('click', (e) => {
    e.stopPropagation();
    h.onSample();
  });
  $('#new-image').addEventListener('click', () => h.onReset());
  $('#error-dismiss').addEventListener('click', () => errorBar.classList.remove('show'));

  // Drag & drop across the whole document.
  let dragDepth = 0;
  const setDrag = (on: boolean) => dropzone.classList.toggle('dragover', on);
  window.addEventListener('dragenter', (e) => {
    e.preventDefault();
    dragDepth++;
    setDrag(true);
  });
  window.addEventListener('dragover', (e) => e.preventDefault());
  window.addEventListener('dragleave', (e) => {
    e.preventDefault();
    dragDepth = Math.max(0, dragDepth - 1);
    if (dragDepth === 0) setDrag(false);
  });
  window.addEventListener('drop', (e) => {
    e.preventDefault();
    dragDepth = 0;
    setDrag(false);
    const f = e.dataTransfer?.files?.[0];
    if (f) h.onFile(f);
  });

  // Paste an image from the clipboard.
  window.addEventListener('paste', (e) => {
    const items = e.clipboardData?.items;
    if (!items) return;
    for (const it of items) {
      if (it.type.startsWith('image/')) {
        const f = it.getAsFile();
        if (f) {
          h.onFile(f);
          break;
        }
      }
    }
  });

  // ── Preset chips ──────────────────────────────────────────────────────
  const presetButtons = new Map<string, HTMLButtonElement>();
  for (const group of ['Mono', 'Colour'] as const) {
    const wrap = document.createElement('div');
    wrap.className = 'preset-group';
    const lbl = document.createElement('span');
    lbl.className = 'preset-group-label';
    lbl.textContent = group;
    wrap.appendChild(lbl);
    for (const p of PRESETS.filter((x) => x.group === group)) {
      const b = document.createElement('button');
      b.type = 'button';
      b.className = 'preset-chip';
      b.textContent = p.label;
      b.title = p.hint;
      b.addEventListener('click', () => {
        setParams({ ...p.params });
        h.onChange();
      });
      presetButtons.set(p.id, b);
      wrap.appendChild(b);
    }
    presetRow.appendChild(wrap);
  }

  // ── Advanced controls ─────────────────────────────────────────────────
  const state: DitherParams = { ...PRESETS[0].params };

  const paletteSel = $<HTMLSelectElement>('#c-palette');
  const algoSel = $<HTMLSelectElement>('#c-algo');
  const colors = $<HTMLInputElement>('#c-colors');
  const pixel = $<HTMLInputElement>('#c-pixel');
  const bright = $<HTMLInputElement>('#c-bright');
  const contrast = $<HTMLInputElement>('#c-contrast');
  const serp = $<HTMLInputElement>('#c-serp');
  const rowColors = $('#row-colors');
  const rowSerp = $('#row-serp');

  // Populate the selects from the label maps (kept DRY).
  for (const key of Object.keys(PALETTE_LABELS) as PaletteId[]) {
    const o = document.createElement('option');
    o.value = key;
    o.textContent = PALETTE_LABELS[key];
    paletteSel.appendChild(o);
  }
  for (const key of Object.keys(ALGO_LABELS) as AlgoId[]) {
    const o = document.createElement('option');
    o.value = key;
    o.textContent = ALGO_LABELS[key];
    algoSel.appendChild(o);
  }

  const readControls = (): DitherParams => ({
    palette: paletteSel.value as PaletteId,
    colors: parseInt(colors.value, 10),
    algorithm: algoSel.value as AlgoId,
    pixelSize: parseInt(pixel.value, 10),
    brightness: parseInt(bright.value, 10),
    contrast: parseInt(contrast.value, 10),
    serpentine: serp.checked,
  });

  const syncColorsRange = () => {
    const { min, max } = colorRange(state.palette);
    colors.min = String(min);
    colors.max = String(max);
    state.colors = clampInt(state.colors, min, max);
    colors.value = String(state.colors);
  };

  const syncOutputs = () => {
    $('#v-colors').textContent = String(state.colors);
    $('#v-pixel').textContent = state.pixelSize === 1 ? '1× (full detail)' : `${state.pixelSize}×`;
    $('#v-bright').textContent = signed(state.brightness);
    $('#v-contrast').textContent = signed(state.contrast);
    rowColors.style.display = usesColorCount(state.palette) ? '' : 'none';
    rowSerp.style.display = isDiffusion(state.algorithm) ? '' : 'none';
    const id = matchPreset(state);
    for (const [pid, btn] of presetButtons) btn.classList.toggle('active', pid === id);
    $('#custom-tag').classList.toggle('show', id === null);
  };

  const writeControls = () => {
    paletteSel.value = state.palette;
    algoSel.value = state.algorithm;
    syncColorsRange();
    pixel.value = String(state.pixelSize);
    bright.value = String(state.brightness);
    contrast.value = String(state.contrast);
    serp.checked = state.serpentine;
    syncOutputs();
  };

  const onControlInput = () => {
    const prevPalette = state.palette;
    Object.assign(state, readControls());
    if (state.palette !== prevPalette) syncColorsRange();
    syncOutputs();
    h.onChange();
  };
  paletteSel.addEventListener('change', onControlInput);
  algoSel.addEventListener('change', onControlInput);
  for (const el of [colors, pixel, bright, contrast, serp]) {
    el.addEventListener('input', onControlInput);
    el.addEventListener('change', onControlInput);
  }

  // ── Action buttons ────────────────────────────────────────────────────
  $('#act-download').addEventListener('click', () => h.onDownload());
  $('#act-copy').addEventListener('click', () => h.onCopy());
  shareBtn.addEventListener('click', () => h.onShare());

  // ── Compare slider ────────────────────────────────────────────────────
  const applySlider = () => {
    const v = Number(slider.value);
    rasterImg.style.clipPath = `inset(0 ${100 - v}% 0 0)`;
    compare.style.setProperty('--split', `${v}%`);
  };
  slider.addEventListener('input', applySlider);

  // ── Modals ────────────────────────────────────────────────────────────
  const openModal = (title: string, bodyHtml: string) => {
    modalHost.innerHTML = `
      <div class="modal-backdrop" data-close></div>
      <div class="modal" role="dialog" aria-modal="true" aria-label="${title}">
        <div class="modal-head">
          <h2>${title}</h2>
          <button class="icon-btn" data-close aria-label="Close">${X_ICON}</button>
        </div>
        <div class="modal-body">${bodyHtml}</div>
      </div>`;
    modalHost.hidden = false;
    modalHost.querySelectorAll('[data-close]').forEach((el) =>
      el.addEventListener('click', closeModal),
    );
    wireGlossary(modalHost);
    (modalHost.querySelector('.icon-btn') as HTMLElement)?.focus();
  };
  const closeModal = () => {
    modalHost.hidden = true;
    modalHost.innerHTML = '';
  };
  $('#nav-how').addEventListener('click', () => openModal('How it works', HOW_HTML));
  $('#nav-privacy').addEventListener('click', () => openModal('Privacy', PRIVACY_HTML));
  $('#nav-about').addEventListener('click', () => openModal('About Ditherwell', ABOUT_HTML));
  $('#trust-banner').addEventListener('click', () => openModal('Privacy', PRIVACY_HTML));

  // ── Event drawer ──────────────────────────────────────────────────────
  const disposeTrail = mountEventTrail(drawerTrail);
  const setDrawer = (open: boolean) => {
    drawer.classList.toggle('open', open);
    drawer.setAttribute('aria-hidden', String(!open));
    $('#drawer-toggle').setAttribute('aria-expanded', String(open));
  };
  $('#drawer-toggle').addEventListener('click', () => setDrawer(!drawer.classList.contains('open')));
  $('#drawer-close').addEventListener('click', () => setDrawer(false));

  // ── Global keyboard ───────────────────────────────────────────────────
  window.addEventListener('keydown', (e) => {
    if (e.key === 'Escape') {
      if (closeOverlays()) e.preventDefault();
    }
  });

  const closeOverlays = (): boolean => {
    if (!modalHost.hidden) {
      closeModal();
      return true;
    }
    if (drawer.classList.contains('open')) {
      setDrawer(false);
      return true;
    }
    const tip = document.getElementById('glossary-tip');
    if (tip && tip.classList.contains('show')) {
      tip.classList.remove('show');
      return true;
    }
    return false;
  };

  wireGlossary(root);

  // ── Public API ────────────────────────────────────────────────────────
  function setParams(p: DitherParams) {
    Object.assign(state, p);
    writeControls();
  }

  function showResult(res: DitherResult, outUrl: string, rasterUrl: string) {
    rasterImg.src = rasterUrl;
    outImg.src = outUrl;
    slider.value = '50';
    applySlider();
    statBar.innerHTML = `
      <span class="stat"><b>${res.width}×${res.height}</b><i>output px</i></span>
      <span class="stat"><b>${res.colors}</b><i>${g('palette')} colour${res.colors === 1 ? '' : 's'}</i></span>
      <span class="stat"><b>${res.algorithm}</b><i>algorithm</i></span>
      <span class="stat"><b>${formatBytes(res.bytes)}</b><i>PNG size</i></span>
      <span class="stat"><b>${res.ms} ms</b><i>dithered @ ${res.artWidth}×${res.artHeight}</i></span>`;
    wireGlossary(statBar);
  }

  function toast(msg: string) {
    toastEl.textContent = msg;
    toastEl.classList.add('show');
    window.clearTimeout((toast as unknown as { t?: number }).t);
    (toast as unknown as { t?: number }).t = window.setTimeout(
      () => toastEl.classList.remove('show'),
      2200,
    );
  }

  return {
    getParams: () => ({ ...state }),
    setParams,
    showStage(stage) {
      stageEmpty.hidden = stage !== 'empty';
      stageWorking.hidden = stage !== 'working';
    },
    setBusy(on, phase, pct) {
      busy.classList.toggle('show', on);
      busy.setAttribute('aria-hidden', String(!on));
      if (phase) busyLabel.textContent = phase;
      const indeterminate = pct == null;
      busyBar.classList.toggle('indeterminate', indeterminate);
      busyBar.style.setProperty('--pct', `${pct ?? 0}%`);
    },
    showResult,
    setError(msg) {
      errorMsg.textContent = msg;
      errorBar.classList.add('show');
    },
    clearError() {
      errorBar.classList.remove('show');
    },
    toast,
    setShareVisible(on) {
      shareBtn.hidden = !on;
    },
    setFileName(name) {
      fileNameEl.textContent = name;
    },
    closeOverlays,
  };

  void disposeTrail;
  void eventCount;
}

function signed(n: number): string {
  return n > 0 ? `+${n}` : `${n}`;
}

// ── Glossary tooltip wiring (shared) ─────────────────────────────────────
function wireGlossary(scope: HTMLElement): void {
  let tip = document.getElementById('glossary-tip');
  if (!tip) {
    tip = document.createElement('div');
    tip.id = 'glossary-tip';
    document.body.appendChild(tip);
    document.addEventListener('click', (e) => {
      if (!(e.target as HTMLElement).closest('.glossary-link') && tip)
        tip.classList.remove('show');
    });
  }
  const show = (el: HTMLElement) => {
    const term = (el.dataset.term || '').toLowerCase();
    const def = GLOSSARY[term];
    if (!def || !tip) return;
    tip.textContent = def;
    const r = el.getBoundingClientRect();
    tip.classList.add('show');
    const tw = Math.min(300, window.innerWidth - 24);
    tip.style.width = `${tw}px`;
    let left = r.left + r.width / 2 - tw / 2;
    left = Math.max(12, Math.min(left, window.innerWidth - tw - 12));
    tip.style.left = `${left}px`;
    tip.style.top = `${r.bottom + 8}px`;
  };
  scope.querySelectorAll<HTMLElement>('.glossary-link').forEach((el) => {
    if (el.dataset.wired) return;
    el.dataset.wired = '1';
    el.addEventListener('click', (e) => {
      e.stopPropagation();
      show(el);
    });
    el.addEventListener('keydown', (e) => {
      if (e.key === 'Enter' || e.key === ' ') {
        e.preventDefault();
        show(el);
      }
    });
  });
}

// ── Static markup ────────────────────────────────────────────────────────
const X_ICON = `<svg viewBox="0 0 24 24" width="20" height="20" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round"><line x1="6" y1="6" x2="18" y2="18"/><line x1="18" y1="6" x2="6" y2="18"/></svg>`;

const LOGO = `<svg viewBox="0 0 64 64" width="26" height="26" aria-hidden="true"><rect width="64" height="64" rx="14" fill="var(--accent-primary)"/><g fill="#fff"><rect x="9" y="9" width="10" height="10" rx="1.6"/><rect x="9" y="21" width="10" height="10" rx="1.6"/><rect x="9" y="33" width="10" height="10" rx="1.6"/><rect x="9" y="45" width="10" height="10" rx="1.6"/><rect x="21" y="9" width="10" height="10" rx="1.6"/><rect x="21" y="21" width="10" height="10" rx="1.6"/><rect x="21" y="45" width="10" height="10" rx="1.6"/><rect x="33" y="9" width="10" height="10" rx="1.6"/><rect x="33" y="33" width="10" height="10" rx="1.6"/><rect x="45" y="21" width="10" height="10" rx="1.6"/></g></svg>`;

const SHELL = /* html */ `
<header class="topbar">
  <div class="brand">${LOGO}<span>Ditherwell</span></div>
  <nav class="nav">
    <button id="nav-how" class="nav-btn" type="button">How it works</button>
    <button id="nav-privacy" class="nav-btn" type="button">Privacy</button>
    <button id="nav-about" class="nav-btn" type="button">About</button>
    <button id="drawer-toggle" class="nav-btn ghost" type="button" aria-expanded="false" aria-controls="drawer">
      Log <span class="badge"><b id="ev-count">0</b></span>
    </button>
  </nav>
</header>

<main class="main-content">
  <button id="trust-banner" class="trust-banner" type="button">
    <svg viewBox="0 0 24 24" width="16" height="16" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><path d="M12 22s8-4 8-10V5l-8-3-8 3v7c0 6 8 10 8 10z"/></svg>
    Runs entirely in your browser. Your image never leaves your device.
  </button>

  <div id="error-bar" class="error-bar">
    <span id="error-msg"></span>
    <button id="error-dismiss" class="icon-btn" type="button" aria-label="Dismiss">${X_ICON}</button>
  </div>

  <!-- EMPTY STAGE -->
  <section id="stage-empty" class="stage-empty">
    <div id="dropzone" class="dropzone" tabindex="0" role="button" aria-label="Choose an image to dither">
      <div class="dz-icon"><svg viewBox="0 0 24 24" width="40" height="40" fill="none" stroke="currentColor" stroke-width="1.6" stroke-linecap="round" stroke-linejoin="round"><rect x="3" y="3" width="18" height="18" rx="2"/><path d="M3 9h18M9 3v18"/></svg></div>
      <div class="dz-title">Drop an image, paste, or click to choose</div>
      <div class="dz-sub">PNG · JPG · WEBP · GIF · BMP — turned into retro ${g('dithering')} pixel-art. Nothing is uploaded.</div>
      <button id="sample-btn" class="pill" type="button">Try a sample</button>
    </div>
  </section>

  <!-- WORKING STAGE -->
  <section id="stage-working" class="stage-working" hidden>
    <div class="work-grid">
      <div class="preview-col">
        <div class="preview-head">
          <span id="file-name" class="file-name">image</span>
          <button id="new-image" class="text-btn" type="button">Choose another</button>
        </div>
        <div id="compare" class="compare checker">
          <img id="out-img" class="layer out-layer" alt="Dithered result" />
          <img id="raster-img" class="layer raster-layer" alt="Original image" />
          <div class="split-line"></div>
          <span class="cmp-label left">Original</span>
          <span class="cmp-label right">Dithered</span>
          <input id="slider" class="cmp-slider" type="range" min="0" max="100" value="50" aria-label="Compare original and dithered" />
          <div id="busy" class="busy" aria-hidden="true">
            <div class="busy-inner">
              <div id="busy-bar" class="busy-bar"><span></span></div>
              <div id="busy-label" class="busy-label">Dithering…</div>
            </div>
          </div>
        </div>
        <div id="stat-bar" class="stat-bar"></div>
      </div>

      <aside class="controls-col">
        <div class="panel">
          <div class="panel-title">Preset <span id="custom-tag" class="custom-tag">Custom</span></div>
          <div id="preset-row" class="preset-row"></div>
        </div>

        <div id="controls" class="panel">
          <div class="panel-title">Adjust</div>

          <div class="ctl">
            <div class="ctl-label"><span>${g('palette')}</span></div>
            <select id="c-palette" class="select" aria-label="Palette"></select>
          </div>

          <div class="ctl" id="row-colors">
            <div class="ctl-label"><span>Colours</span><output id="v-colors">4</output></div>
            <input id="c-colors" type="range" min="2" max="32" step="1" value="4">
          </div>

          <div class="ctl">
            <div class="ctl-label"><span>Algorithm</span></div>
            <select id="c-algo" class="select" aria-label="Dithering algorithm"></select>
          </div>

          <div class="ctl">
            <div class="ctl-label"><span>${g('pixel size')}</span><output id="v-pixel">1× (full detail)</output></div>
            <input id="c-pixel" type="range" min="1" max="12" step="1" value="1">
          </div>

          <div class="ctl">
            <div class="ctl-label"><span>Brightness</span><output id="v-bright">0</output></div>
            <input id="c-bright" type="range" min="-100" max="100" step="1" value="0">
          </div>

          <div class="ctl">
            <div class="ctl-label"><span>Contrast</span><output id="v-contrast">0</output></div>
            <input id="c-contrast" type="range" min="-100" max="100" step="1" value="0">
          </div>

          <div class="ctl checkline" id="row-serp">
            <label class="check"><input type="checkbox" id="c-serp"><span>${g('serpentine')} scan</span></label>
          </div>
        </div>

        <div class="actions">
          <button id="act-download" class="btn primary" type="button">Download PNG</button>
          <div class="actions-row">
            <button id="act-copy" class="btn" type="button">Copy PNG</button>
            <button id="act-share" class="btn" type="button" hidden>Share</button>
          </div>
        </div>
      </aside>
    </div>
  </section>
</main>

<footer class="site-footer">
  <div class="footer-inner">
    Built by <a href="https://benrichardson.dev/" target="_blank" rel="noopener">benrichardson.dev</a>
    · <a href="https://lab.benrichardson.dev" target="_blank" rel="noopener">more tools &amp; sites</a>
  </div>
</footer>

<aside id="drawer" class="drawer" aria-hidden="true" aria-label="Event log">
  <div class="drawer-head">
    <div class="drawer-title">Event log</div>
    <button id="drawer-close" class="icon-btn" type="button" aria-label="Close event log">${X_ICON}</button>
  </div>
  <div id="drawer-trail" class="drawer-trail"></div>
</aside>

<input id="file-input" type="file" accept="image/png,image/jpeg,image/webp,image/gif,image/bmp,image/avif,image/*" hidden />
<div id="modal-host" hidden></div>
<div id="toast" class="toast" role="status" aria-live="polite"></div>
`;

const HOW_HTML = /* html */ `
<p>Ditherwell turns a full-colour photo into retro, palette-limited pixel-art — entirely inside your browser tab. Here is what happens when you drop a file in:</p>
<ol class="steps">
  <li><b>Decode &amp; shrink.</b> Your image is decoded and down-sampled by the ${g('pixel size')} you choose. A bigger pixel size gives chunkier, more pixel-art results.</li>
  <li><b>Tune tone.</b> Optional brightness and contrast are applied before quantising — often the difference between a muddy and a punchy dither.</li>
  <li><b>Choose a ${g('palette')}.</b> A fixed one (1-bit, Game Boy, CGA, grayscale) or an <i>adaptive</i> one picked straight from your image with ${g('median cut')}.</li>
  <li><b>Dither.</b> ${g('error diffusion')} (Floyd–Steinberg, ${g('atkinson')}, Stucki…) or ${g('ordered')} Bayer dithering scatters the palette colours so your eye blends the shades that are missing.</li>
  <li><b>Export.</b> The result is scaled back up with crisp, square pixels and handed to you as a PNG to download, copy or share.</li>
</ol>
<p class="muted">Every step runs on your device. There is no server, no upload and no account.</p>
`;

const PRIVACY_HTML = /* html */ `
<div class="tm">
  <div class="tm-block ok">
    <h3>Protected</h3>
    <ul>
      <li>Your image is decoded, down-sampled and dithered entirely on your device. It is <b>never uploaded</b>.</li>
      <li>The output PNG is generated locally and only leaves your device if <i>you</i> download, copy or share it.</li>
      <li>The whole app is served from this origin and runs offline once cached — no image ever touches a network.</li>
      <li>No cookies, no fingerprinting, no third-party fonts.</li>
    </ul>
  </div>
  <div class="tm-block warn">
    <h3>Not protected</h3>
    <ul>
      <li>The initial page load and static assets are served by GitHub Pages, whose CDN sees the request for the <i>page</i> — never your image.</li>
      <li>Anything you deliberately export (download / copy / share) is then in your hands and out of Ditherwell's control.</li>
    </ul>
  </div>
  <div class="tm-block trust">
    <h3>Trust surface</h3>
    <ul>
      <li>The static site bundle (hash-pinned by the GitHub Pages deploy).</li>
      <li>The TLS connection between you and GitHub Pages.</li>
      <li>A Cloudflare Web Analytics beacon records anonymous page views — no cookies, no fingerprinting, no cross-site tracking; your images are never sent to it.</li>
      <li>Feedback you choose to send is sent to feedback.benrichardson.dev. Nothing is sent unless you open the feedback form and press Send; your files and data never are.</li>
    </ul>
  </div>
</div>
`;

const ABOUT_HTML = /* html */ `
<p><b>Ditherwell</b> is a free, private retro image ditherer. Drop in a photo and get back 1-bit black &amp; white, the four-green Game Boy look, CGA, grayscale, or an adaptive palette drawn from your own image — all with your choice of ${g('dithering')} algorithm.</p>
<p>It exists because the classic dithered aesthetic is everywhere — zines, e-ink and thermal-printer art, indie-game sprites, album covers — but the online tools either upload your picture, watermark it, or only offer one algorithm.</p>
<p>Made by <a href="https://benrichardson.dev/" target="_blank" rel="noopener">Ben Richardson</a>. Part of a small lab of privacy-first browser tools — see them all at <a href="https://lab.benrichardson.dev" target="_blank" rel="noopener">lab.benrichardson.dev</a>.</p>
<p class="muted">The dithering (${g('error diffusion')}, ${g('ordered')} Bayer and ${g('median cut')} palette extraction) is all first-party code — no libraries. Source: <a href="https://github.com/ben-gy/ditherwell" target="_blank" rel="noopener">github.com/ben-gy/ditherwell</a>.</p>
<p class="muted">Shortcuts: <kbd>Esc</kbd> closes dialogs · <kbd>Ctrl/⌘</kbd>+<kbd>V</kbd> pastes an image · drag a file anywhere onto the page.</p>
`;
